import React, { useState } from 'react';
import styled from 'styled-components';
import { FaFilter, FaChevronDown, FaSearch, FaTimes } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';

const FilterDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 1000;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.inputBg};
  border: 1.5px solid ${props => props.theme.primary};
  border-radius: 0.4rem;
  padding: 0.45rem 0.9rem;
  font-size: 0.95rem;
  color: ${props => props.theme.primaryText};
  cursor: pointer;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 100px;
  justify-content: center;
  
  &:hover, &:focus {
    border: 1.5px solid ${props => props.theme.primaryHover};
    outline: none;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.5rem 0.75rem;
    min-width: 88px;
  }
  
  @media (max-width: 600px) {
    min-width: 0;
    padding: 0.5rem 0.65rem;
    height: 2.5rem;
    min-height: 2.5rem;
  }
`;

const FilterLabel = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const FilterIcon = styled(FaFilter)`
  color: ${props => props.theme.primary};
  font-size: 1.1em;
`;

const ChevronIcon = styled(FaChevronDown)`
  color: ${props => props.theme.mutedText};
  font-size: 0.9em;
  transition: transform 0.2s;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const FilterCount = styled.span`
  background-color: ${props => props.theme.primary};
  color: ${props => props.theme.inverseText || '#fff'};
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  
  @media (max-width: 480px) {
    min-width: 16px;
    height: 16px;
    font-size: 0.65rem;
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  left: auto;
  background: ${props => props.theme.cardBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadowHover || '0 4px 12px rgba(0,0,0,0.25)'};
  z-index: 1001;
  min-width: 220px;
  max-width: min(280px, calc(100vw - 2rem));
  max-height: min(70vh, 420px);
  overflow-y: auto;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  
  @media (max-width: 480px) {
    left: 0.5rem;
    right: 0.5rem;
    min-width: 0;
    max-width: none;
    width: calc(100vw - 1rem);
  }
`;

const PanelSection = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
  &:last-child {
    border-bottom: none;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${props => props.theme.secondaryText};
  margin-bottom: 0.5rem;
`;

const OptionsActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
  font-size: 0.75rem;
`;

const OptionsLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  color: ${props => props.theme.mutedText || props.theme.secondaryText};
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: ${props => props.theme.primary};
    text-decoration: underline;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;
const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 0.65rem;
  color: ${props => props.theme.mutedText};
  font-size: 0.85rem;
  pointer-events: none;
`;
const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem ${props => props.$hasClear ? '2rem' : '0.75rem'} 0.5rem 2rem;
  font-size: 0.9rem;
  background: ${props => props.theme.inputBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.35rem;
  color: ${props => props.theme.primaryText};
  &::placeholder {
    color: ${props => props.theme.mutedText};
  }
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: ${props => props.theme.mutedText};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  &:hover {
    color: ${props => props.theme.primaryText};
    background: ${props => props.theme.tertiaryBg};
  }
  &:focus {
    outline: none;
  }
`;

const TeamSelect = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  background: ${props => props.theme.inputBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.35rem;
  color: ${props => props.theme.primaryText};
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${props => props.theme.primaryText};
  
  &:hover {
    color: ${props => props.theme.primaryText};
  }
  
  input {
    cursor: pointer;
    margin: 0;
    flex-shrink: 0;
  }
`;

const FilterDropdown = ({
  filters,
  onFilterChange,
  playerSearch = '',
  onPlayerSearchChange,
  teamFilter = '',
  onTeamFilterChange,
  teamOptions = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();

  const checkboxActiveCount = Object.values(filters).filter(Boolean).length;
  const searchActive = typeof playerSearch === 'string' && playerSearch.trim().length > 0;
  const teamActive = typeof teamFilter === 'string' && teamFilter.trim().length > 0;
  const activeFiltersCount = checkboxActiveCount + (searchActive ? 1 : 0) + (teamActive ? 1 : 0);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleFilterChange = (key, value) => {
    onFilterChange(key, value);
  };

  const handleClickOutside = (e) => {
    if (!e.target.closest('.filter-dropdown')) {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const teams = Array.isArray(teamOptions) ? teamOptions : [];
  const hasSearchOrTeam = onPlayerSearchChange != null || onTeamFilterChange != null;

  return (
    <FilterDropdownContainer className="filter-dropdown">
      <FilterButton onClick={handleToggle} theme={theme} type="button" aria-haspopup="dialog" aria-expanded={isOpen}>
        <FilterIcon theme={theme} />
        <FilterLabel>Filters</FilterLabel>
        {activeFiltersCount > 0 && (
          <FilterCount theme={theme}>{activeFiltersCount}</FilterCount>
        )}
        <ChevronIcon $isOpen={isOpen} theme={theme} />
      </FilterButton>

      <DropdownContent $isOpen={isOpen} theme={theme} role="dialog" aria-label="Filters">
        {hasSearchOrTeam && (
          <PanelSection theme={theme}>
            <SectionLabel theme={theme}>Search &amp; filter</SectionLabel>
            {onPlayerSearchChange != null && (
              <SearchWrap style={{ marginBottom: teamOptions.length > 0 ? '0.5rem' : 0 }}>
                <SearchIcon theme={theme} aria-hidden />
                <SearchInput
                  type="text"
                  theme={theme}
                  $hasClear={typeof playerSearch === 'string' && playerSearch.trim().length > 0}
                  value={playerSearch}
                  onChange={(e) => onPlayerSearchChange(e.target.value)}
                  placeholder="Search by player name"
                  aria-label="Search by player name"
                />
                {typeof playerSearch === 'string' && playerSearch.trim().length > 0 && (
                  <ClearButton
                    type="button"
                    theme={theme}
                    onClick={() => onPlayerSearchChange('')}
                    aria-label="Clear player search"
                  >
                    <FaTimes size={14} />
                  </ClearButton>
                )}
              </SearchWrap>
            )}
            {onTeamFilterChange != null && teams.length > 0 && (
              <>
                <TeamSelect
                  theme={theme}
                  value={teamFilter}
                  onChange={(e) => onTeamFilterChange(e.target.value)}
                  aria-label="Filter by team"
                >
                  <option value="">All teams</option>
                  {teams.map((opt) => {
                    const value = typeof opt === 'string' ? opt : opt.value;
                    const label = typeof opt === 'string' ? opt : (opt.label || opt.value);
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </TeamSelect>
              </>
            )}
          </PanelSection>
        )}

        <PanelSection theme={theme}>
          <SectionLabel theme={theme}>Options</SectionLabel>
          <OptionsActions theme={theme}>
            <OptionsLink
              type="button"
              theme={theme}
              onClick={() => {
                ['inLineup', 'featuredPicks', 'top5', 'hasntStarted', 'hasntEnded'].forEach((key) =>
                  onFilterChange(key, false)
                );
                if (typeof onPlayerSearchChange === 'function') {
                  onPlayerSearchChange('');
                }
                if (typeof onTeamFilterChange === 'function') {
                  onTeamFilterChange('');
                }
              }}
              aria-label="Clear all filters, player search, and team selection"
            >
              Clear all
            </OptionsLink>
          </OptionsActions>
          <FilterOption theme={theme}>
            <input
              type="checkbox"
              checked={filters.inLineup}
              onChange={(e) => handleFilterChange('inLineup', e.target.checked)}
            />
            In lineup
          </FilterOption>
          <FilterOption theme={theme}>
            <input
              type="checkbox"
              checked={filters.featuredPicks}
              onChange={(e) => handleFilterChange('featuredPicks', e.target.checked)}
            />
            Featured Picks
          </FilterOption>
          <FilterOption theme={theme}>
            <input
              type="checkbox"
              checked={filters.top5}
              onChange={(e) => handleFilterChange('top5', e.target.checked)}
            />
            Top 5
          </FilterOption>
          <FilterOption theme={theme}>
            <input
              type="checkbox"
              checked={filters.hasntStarted}
              onChange={(e) => handleFilterChange('hasntStarted', e.target.checked)}
            />
            Hasn't Started
          </FilterOption>
          <FilterOption theme={theme}>
            <input
              type="checkbox"
              checked={filters.hasntEnded}
              onChange={(e) => handleFilterChange('hasntEnded', e.target.checked)}
            />
            Hasn't Ended
          </FilterOption>
        </PanelSection>
      </DropdownContent>
    </FilterDropdownContainer>
  );
};

export default FilterDropdown;
